import type { ReactiveSet } from '@solid-primitives/set'
import { Title } from '@solidjs/meta'
import { type Component, createEffect, createMemo, createResource, createSignal, Show } from 'solid-js'
import { createStore } from 'solid-js/store'
import Badge from '~/components/Badge'
import Button from '~/components/Button'
import ButtonGroup from '~/components/ButtonGroup'
import Card from '~/components/Card'
import Checkbox from '~/components/Checkbox'
import InfiniteList from '~/components/InfiniteList'
import Input from '~/components/Input'
import Select from '~/components/Select'
import SwipeAction from '~/components/SwipeAction'
import Table from '~/components/Table'
import useAlert from '~/components/useAlert'
import { useToast } from '~/components/useToast'
import { useCompany } from '~/contexts/CompanyContext'
import { useDebouncedSignal } from '~/hooks/useDebouncedSignal'
import { useIsMobile } from '~/hooks/useIsMobile'
import { usePermissions } from '~/hooks/usePermissions'
import { useServerTable } from '~/hooks/useServerTable'
import TableFilters, { FiltersButton } from '~/layers/TableFilters'
import { companyService } from '~/services/company.service'
import { type LeadType, leadService } from '~/services/lead.service'
import { resolveError } from '~/utils/error'
import { P } from '~/utils/permissions'

const SOURCE_LABELS: Record<string, string> = {
  'chat-web': 'Asesor web',
  'chat-admin': 'Chat interno',
  chat: 'Chat'
}

const ProspectosPage: Component = () => {
  const toast = useToast()
  const { can, isRoot } = usePermissions()
  const alert = useAlert()
  const { currentCompany, companies } = useCompany()
  const isMobile = useIsMobile()

  /** Empresa destino elegida en el diálogo de asignación (solo root). */
  const [assignCompanyId, setAssignCompanyId] = createSignal<string>('')

  // Root puede adoptar para CUALQUIER empresa de la plataforma, no solo las suyas.
  const [allCompanies] = createResource(
    () => (isRoot() ? 'load' : null),
    async () => {
      try {
        const result = await companyService.list({ limit: 200 })
        return result.items
      } catch {
        return []
      }
    }
  )

  const assignCompanyOptions = createMemo(() => {
    const list = isRoot() ? (allCompanies() ?? []) : companies().map(c => c.company)
    return list.map(c => ({ value: c.id, title: c.name }))
  })

  // Búsqueda y filtros server-side
  const [filtersOpen, setFiltersOpen] = createSignal(false)
  const [filterSource, setFilterSource] = createSignal('')
  const [filterSearch, debouncedSearch, setFilterSearch] = useDebouncedSignal('')

  const buildParams = (): Record<string, string | number | undefined> => {
    const q = debouncedSearch().trim()
    const p: Record<string, string | number | undefined> = {}
    if (q) p.name = `%${q}%`
    if (filterSource()) p.source = filterSource()
    return p
  }

  const table = useServerTable<LeadType>({
    fetcher: params => leadService.list(params as Parameters<typeof leadService.list>[0]),
    params: buildParams
  })

  // ── Scroll infinito móvil ────────────────────────────────────────────────
  const [mobileItems, setMobileItems] = createStore<LeadType[]>([])
  const [mobileLoading, setMobileLoading] = createSignal(false)
  const [mobileTotal, setMobileTotal] = createSignal(0)
  const hasMore = () => mobileItems.length < mobileTotal()

  const mobileFetch = async (reset: boolean) => {
    if (!reset && mobileLoading()) return
    setMobileLoading(true)
    try {
      const page = reset ? 1 : Math.floor(mobileItems.length / table.perPage()) + 1
      const res = await leadService.list({ ...buildParams(), page, limit: table.perPage() })
      setMobileTotal(res.meta.total)
      setMobileItems(reset ? res.items : prev => [...prev, ...res.items])
    } catch {
      // error silencioso: el sentinela reintentará en el siguiente scroll
    } finally {
      setMobileLoading(false)
    }
  }

  // Reinicia la lista (y recarga desde la página 1) cuando cambian los filtros.
  // Al entrar en móvil la clave pasa de '' a los filtros actuales, así que este
  // mismo efecto hace la primera carga sin duplicar peticiones.
  const [filtersKey, setFiltersKey] = createSignal('')
  createEffect(() => {
    if (!isMobile()) return
    const key = JSON.stringify([filterSource(), debouncedSearch()])
    if (key === filtersKey()) return
    setFiltersKey(key)
    void mobileFetch(true)
  })

  const loadMore = () => {
    if (!hasMore() || mobileLoading()) return
    void mobileFetch(false)
  }

  const activeFilters = createMemo(() => [filterSource()].filter(v => v !== '').length + (filterSearch() ? 1 : 0))

  const clearFilters = () => {
    setFilterSource('')
    setFilterSearch('')
  }

  const leadChips = createMemo(() => [
    ...(filterSearch() ? [{ label: `«${filterSearch()}»`, color: 'info' as const, icon: 'material-symbols-light:search', onRemove: () => setFilterSearch('') }] : []),
    ...(filterSource() ? [{ label: SOURCE_LABELS[filterSource()] ?? filterSource(), color: 'primary' as const, onRemove: () => setFilterSource('') }] : [])
  ])

  const handleDelete = async (item: LeadType) => {
    const ok = await alert.confirmDelete({
      title: 'Eliminar prospecto',
      message: `¿Eliminar el prospecto "${item.name}"?`
    })
    if (!ok) return
    try {
      await leadService.delete(item.id)
      toast.addToast({ title: 'Prospecto eliminado', type: 'success' })
      // En la vista móvil la lista se acumula en `mobileItems`: quitarla también
      // de ahí, o el `refetch` (misma página) no la actualizaría.
      setMobileItems(prev => prev.filter(i => i.id !== item.id))
      await table.refetch()
    } catch (err: unknown) {
      toast.addToast({ title: resolveError(err), type: 'danger' })
    }
  }

  /** Adopta un prospecto sin empresa. Root elige el destino; el resto, la activa. */
  const handleAssign = async (item: LeadType) => {
    setAssignCompanyId(currentCompany().id ?? '')
    const ok = await alert.confirm({
      type: 'confirm',
      title: 'Asignar prospecto a una empresa',
      message: `"${item.name}" pasará a ser de la empresa elegida y aparecerá en su tablero del CRM como prospecto nuevo. ¿Continuar?`,
      body: isRoot() ? (
        <div class="space-y-1.5">
          <label class="text-sm font-medium text-foreground">Empresa destino</label>
          <Select options={assignCompanyOptions()} value={assignCompanyId()} onChange={v => setAssignCompanyId(String(v ?? ''))} />
        </div>
      ) : undefined,
      confirmText: 'Sí, asignar',
      confirmIcon: 'material-symbols-light:domain-add-outline'
    })
    if (!ok) return
    try {
      await leadService.assign(item.id, isRoot() ? assignCompanyId() : undefined)
      toast.addToast({ title: `"${item.name}" asignado`, type: 'success' })
      await table.refetch()
    } catch (err: unknown) {
      toast.addToast({ title: resolveError(err), type: 'danger' })
    }
  }

  // ── Selección masiva ────────────────────────────────────────────────────
  const [selectedIds, setSelectedIds] = createSignal<ReadonlySet<number>>(new Set())

  /** La Table gestiona sus checkboxes; aquí se espeja lo marcado para el banner
   *  móvil y para saber qué borrar. La selección es por página. */
  const handleSelectionChange = (selected: ReactiveSet<unknown>) => {
    const next = new Set<number>()
    for (const value of selected.values()) next.add(value as number)
    setSelectedIds(next)
  }

  const toggleSelect = (id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const handleBulkDelete = async () => {
    const ids = [...selectedIds()]
    if (ids.length === 0) return
    const ok = await alert.confirmDelete({
      title: 'Eliminar prospectos',
      message: `¿Eliminar ${ids.length} ${ids.length === 1 ? 'prospecto' : 'prospectos'}? Esta acción no se puede deshacer.`
    })
    if (!ok) return
    try {
      const res = await leadService.bulkDelete(ids)
      toast.addToast({ title: `${res.deleted} ${res.deleted === 1 ? 'prospecto eliminado' : 'prospectos eliminados'}`, type: 'success' })
      setSelectedIds(new Set<number>())
      // En la vista móvil la lista se acumula en `mobileItems`: quitarlas también
      // de ahí, o el `refetch` (misma página) no la actualizaría.
      setMobileItems(prev => prev.filter(i => !ids.includes(i.id)))
      await table.refetch()
    } catch (err: unknown) {
      toast.addToast({ title: resolveError(err), type: 'danger' })
    }
  }

  const formatDate = (value?: string | null) => {
    if (!value) return '-'
    const date = new Date(value)
    return Number.isNaN(date.getTime()) ? '-' : date.toLocaleString('es-CO')
  }

  const columns = [
    { header: 'Nombre', accessor: (l: LeadType) => l.name },
    { header: 'Correo', accessor: (l: LeadType) => l.email ?? '-' },
    { header: 'Teléfono', accessor: (l: LeadType) => l.phone ?? '-' },
    { header: 'Interés', accessor: (l: LeadType) => l.interest ?? '-' },
    { header: 'Origen', accessor: (l: LeadType) => SOURCE_LABELS[l.source ?? ''] ?? l.source ?? '-' },
    {
      header: 'Empresa',
      accessor: (l: LeadType) =>
        l.companyId ? (
          <span class="text-foreground-subtle">Asignado</span>
        ) : (
          <Badge color="warning" variant="soft">
            Sin empresa
          </Badge>
        )
    },
    { header: 'Fecha', accessor: (l: LeadType) => formatDate(l.createdAt) }
  ]

  return (
    <>
      <Title>Prospectos | Florez Jaramillo</Title>
      <Show when={table.error()}>
        <div class="rounded-xl border border-danger-soft bg-danger-soft/30 px-4 py-3 text-sm text-danger mb-4">{resolveError(table.error())}</div>
      </Show>

      <Show when={!isMobile()}>
        <Table<LeadType>
          title="Prospectos"
          subtitle="Contactos que dejó el asesor de la web. Se capturan solos cuando un visitante muestra interés."
          headerActions={<FiltersButton activeCount={activeFilters()} onClick={() => setFiltersOpen(true)} />}
          data={table.data()}
          columns={columns}
          keyField="id"
          headSticky
          loading={table.loading()}
          serverSide
          selectable={can(P.leads.delete)}
          selectableColor="danger"
          onSelectionChange={handleSelectionChange}
          onDeleteSelected={() => void handleBulkDelete()}
          total={table.total()}
          page={table.page()}
          perPage={table.perPage()}
          onPageChange={table.onPageChange}
          onPerPageChange={table.onPerPageChange}
          emptyMessage="Todavía no hay prospectos. Aparecerán aquí cuando el asesor capture un contacto."
          actions={item => (
            <ButtonGroup size="sm" variant="soft">
              <Show when={item.email}>
                <Button color="info" onClick={() => window.open(`mailto:${item.email}`)} tooltip="Escribir" icon="material-symbols-light:mail-outline" />
              </Show>
              <Show when={!item.companyId && can(P.leads.update)}>
                <Button color="primary" onClick={() => handleAssign(item)} tooltip="Asignar a mi empresa" icon="material-symbols-light:domain-add-outline" />
              </Show>
              <Show when={can(P.leads.delete)}>
                <Button color="danger" onClick={() => handleDelete(item)} tooltip="Eliminar" icon="material-symbols-light:delete-outline" />
              </Show>
            </ButtonGroup>
          )}
        />
      </Show>

      {/* Vista móvil: en pantallas pequeñas la tabla desborda, así que se
          sustituye por tarjetas con acciones deslizables y scroll infinito. */}
      <Show when={isMobile()}>
        <Show when={can(P.leads.delete) && selectedIds().size > 0}>
          <div class="mb-3 flex items-center justify-between gap-2 rounded-xl border border-danger-soft bg-danger-soft/30 px-4 py-3">
            <span class="text-sm font-semibold text-danger">
              {selectedIds().size} {selectedIds().size === 1 ? 'seleccionado' : 'seleccionados'}
            </span>
            <Button size="sm" color="danger" variant="soft" icon="material-symbols-light:delete-outline" onClick={() => void handleBulkDelete()}>
              Eliminar
            </Button>
          </div>
        </Show>
        <InfiniteList<LeadType>
          items={mobileItems}
          keyField={item => item.id}
          hasMore={hasMore()}
          loading={mobileLoading()}
          onLoadMore={loadMore}
          onRefresh={async () => {
            await mobileFetch(true)
          }}
          emptyMessage={<p class="py-10 text-center text-foreground-subtle">Todavía no hay prospectos. Aparecerán aquí cuando el asesor capture un contacto.</p>}
          endMessage={<span>Has llegado al final de los prospectos</span>}
          class="space-y-3"
          renderItem={item => (
            <SwipeAction
              itemId={item.id}
              rememberPosition
              leftOptions={[
                ...(item.email
                  ? [
                      {
                        icon: 'material-symbols-light:mail-outline',
                        label: 'Escribir',
                        color: 'info',
                        action: () => window.open(`mailto:${item.email}`)
                      }
                    ]
                  : []),
                ...(!item.companyId && can(P.leads.update)
                  ? [
                      {
                        icon: 'material-symbols-light:domain-add-outline',
                        label: 'Asignar',
                        color: 'primary',
                        action: () => handleAssign(item)
                      }
                    ]
                  : [])
              ]}
              rightOptions={[
                ...(can(P.leads.delete)
                  ? [
                      {
                        icon: 'material-symbols-light:delete-outline',
                        label: 'Eliminar',
                        color: 'danger',
                        action: () => handleDelete(item)
                      }
                    ]
                  : [])
              ]}
            >
              <Card class="!rounded-lg">
                <Card.Body>
                  <div class="flex items-start gap-3">
                    <Show when={can(P.leads.delete)}>
                      <Checkbox size="sm" color="danger" checked={selectedIds().has(item.id)} onChange={() => toggleSelect(item.id)} class="mt-0.5 shrink-0" />
                    </Show>
                    <div class="min-w-0 flex-1">
                      <div class="flex items-center justify-between gap-2">
                        <p class="font-medium text-foreground truncate">{item.name}</p>
                        <Show
                          when={item.companyId}
                          fallback={
                            <Badge color="warning" variant="soft" class="shrink-0">
                              Sin empresa
                            </Badge>
                          }
                        >
                          <span class="text-xs text-foreground-subtle shrink-0">Asignado</span>
                        </Show>
                      </div>
                      <div class="mt-2 space-y-0.5 text-sm">
                        <p class="text-foreground truncate">{item.email ?? '—'}</p>
                        <p class="text-foreground truncate">{item.phone ?? '—'}</p>
                        <p class="text-xs text-foreground-subtle truncate">{item.interest ?? '—'}</p>
                      </div>
                      <div class="mt-2 flex items-center justify-between gap-2 text-xs text-foreground-subtle">
                        <span>{SOURCE_LABELS[item.source ?? ''] ?? item.source ?? '—'}</span>
                        <span>{formatDate(item.createdAt)}</span>
                      </div>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </SwipeAction>
          )}
        />
      </Show>

      <TableFilters title="Filtros de prospectos" isOpen={filtersOpen()} onClose={() => setFiltersOpen(false)} activeCount={activeFilters()} onClear={clearFilters} chips={leadChips()}>
        <Input label="Buscar" value={filterSearch()} onInput={e => setFilterSearch(e.currentTarget.value)} placeholder="Nombre, correo o teléfono..." startIcon="material-symbols-light:search" />

        <Select
          label="Origen"
          options={[{ value: '', title: 'Cualquier origen' }, ...Object.entries(SOURCE_LABELS).map(([value, title]) => ({ value, title }))]}
          value={filterSource()}
          onChange={v => setFilterSource(String(v ?? ''))}
          class="w-full"
        />
      </TableFilters>

      <alert.AlertComponent />
    </>
  )
}

export default ProspectosPage
